import { Fragment } from 'react';
import { FundInfoData } from '../../../../types/fundsummary.type';
import {
  formatFundMilionValues,
  formatLocaleNumber,
} from '../../../../helpers/number';
import { formatDetailDate } from '../../../../helpers/date';
import './FundInfoList.css';
import CustomTooltip from '../../../../Components/Common/CustomTooltip';

export interface InfoRow {
  label: string;
  value: string;
}

export type InfoGroup = InfoRow[];

export type FundInfoVariant = 'facts' | 'profile';

export interface FundInfoListProps {
  info: FundInfoData;
  variant: FundInfoVariant;
}

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${value.toFixed(2)}%`;
};

const formatText = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value.trim() === '') return '-';
  return value;
};

const buildFacts = (info: FundInfoData): InfoGroup[] => {
  const { fundDetailInfo, fundProjectYield, fundCurrency } = info;
  return [
    [
      {
        label: `Total net asset (${formatDetailDate(fundDetailInfo?.tnaDate, false, 'DMY')})`,
        value: `${formatFundMilionValues(fundDetailInfo?.tna, 2)} ${formatText(
          fundDetailInfo?.tnaCcy,
        )}`,
      },
      {
        label: 'Fund currency',
        value: formatText(fundCurrency?.nameEn),
      },
    ],
    [
      {
        label: 'Ex-dividend date',
        value: formatDetailDate(
          fundDetailInfo?.incomeDividendXDate,
          false,
          'DMY',
        ),
      },
      {
        label: 'Payment date',
        value: formatDetailDate(
          fundDetailInfo?.incomeDividendPaymentDate,
          false,
          'DMY',
        ),
      },
      {
        label: 'Dividend',
        value:
          fundDetailInfo?.incomeDividendXvalue != null
            ? `${formatLocaleNumber(fundDetailInfo.incomeDividendXvalue, 4)} ${formatText(
                fundDetailInfo.incomeDistributionCurr,
              )}`
            : '-',
      },
      {
        label: `Projected yield (${formatDetailDate(fundProjectYield?.projectedYieldPrevMonthendEndDate, false, 'DMY')})`,
        value: formatPercent(fundProjectYield?.projectedYieldPrevMonthend1y),
      },
      {
        label: 'Dividend yield',
        value: formatPercent(fundProjectYield?.yieldPrevMonthend1y),
      },
    ],
  ];
};

const buildProfile = (info: FundInfoData): InfoGroup[] => {
  const { fundDetailInfo } = info;
  return [
    [
      { label: 'Asset type', value: formatText(fundDetailInfo?.enAssetType) },
      { label: 'Classification', value: formatText(fundDetailInfo?.category) },
      {
        label: 'Geographical focus',
        value: formatText(fundDetailInfo?.enGeographicalFocus),
      },
    ],
    [
      {
        label: 'Lipper benchmark',
        value: formatText(fundDetailInfo?.benchmarksLgcBenchmarkName),
      },
      {
        label: 'Fund Manager benchmark',
        value: formatText(fundDetailInfo?.benchmarksFundManagerName),
      },
    ],
    [
      {
        label: 'Management company',
        value: formatText(fundDetailInfo?.fundManagementCompanyName),
      },
      {
        label: 'Fund manager',
        value: formatText(fundDetailInfo?.manager),
      },
      {
        label: 'Administrator',
        value: formatText(fundDetailInfo?.administrator),
      },
    ],
  ];
};

const FundInfoList = ({ info, variant }: FundInfoListProps): JSX.Element => {
  const groups = variant === 'facts' ? buildFacts(info) : buildProfile(info);

  return (
    <div className="fund-info">
      {groups.map((rows, index) => (
        <Fragment key={index}>
          {index > 0 && <div className="fund-info__separator" />}
          <div className="fund-info__group">
            {rows.map((row) => (
              <div className="fund-info__row" key={row.label}>
                <span className="fund-info__label">{row.label}</span>
                <CustomTooltip title={row.value} placement="auto-end">
                  <span className="fund-info__value">{row.value}</span>
                </CustomTooltip>
              </div>
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
};

export default FundInfoList;
